import { Navigate, Outlet, useLocation } from 'react-router-dom';

import {
  defaultPathForRole,
  useSession,
  type UserRole
} from '@/components/auth/user-provider';

export type RequireRoleProps = {
  /** Roles allowed to render the nested routes. */
  roles: readonly UserRole[];
};

/**
 * Route guard for role-based areas. Unauthenticated traffic is handled by
 * AppShell; this only redirects signed-in users with the wrong role/status.
 *
 * Frontend gating is UX only — the API still enforces roles server-side.
 */
export function RequireRole({ roles }: RequireRoleProps) {
  const { session } = useSession();
  const location = useLocation();

  if (session.status === 'loading') {
    return null;
  }

  if (session.status !== 'signedIn') {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  const { user } = session;

  if (!user.isActive) {
    return <Navigate to="/access-blocked" replace />;
  }

  if (user.role === 'pending') {
    return <Navigate to="/awaiting-access" replace />;
  }

  if (!roles.includes(user.role)) {
    return <Navigate to={defaultPathForRole(user.role, user.isActive)} replace />;
  }

  return <Outlet />;
}

/** Admin-only area (settings + /admin). */
export function RequireAdmin() {
  return <RequireRole roles={['admin']} />;
}

/** Staff operational screens (events, students, register, etc.). */
export function RequireStaff() {
  return <RequireRole roles={['admin', 'teacher']} />;
}
